'use client';

import { forwardRef } from 'react';
import type { IconType } from 'react-icons';

import type { PolymorphicForwardRefComponent } from '../utils/types';
import styles from './plain-button.component.module.scss';

type PlainButtonProps = {
  /** IconをButtonテキストの先頭に設置します。 */
  startIcon?: IconType;
  /** IconをButtonテキストの末尾に設置します。 */
  endIcon?: IconType;
};

/**
 * スタイルを持たないButtonコンポーネント。
 * yuffie-uiのButtonではカバーできないUIパターンで利用することを想定しています。
 *
 * @remark <button>要素として使用する場合、type="button" がデフォルトで設定されます。
 * @see {@link https://github.com/Findy/Yuffie/issues/8620#issuecomment-1584070337}
 */
export const PlainButton = forwardRef(
  (
    {
      children,
      as: component = 'button',
      startIcon: StartIcon,
      endIcon: EndIcon,
      type,
      className,
      ...rest
    },
    forwardedRef
  ) => {
    const Root = component;

    return (
      <Root
        className={[styles.buttonBase, className].filter(Boolean).join(' ')}
        ref={forwardedRef} // forwardRefでHTMLAnchorElementでもrefを利用する
        type={component === 'button' && type === undefined ? 'button' : type}
        {...rest}
      >
        {StartIcon && (
          <span className={styles.iconBox}>
            <StartIcon />
          </span>
        )}
        {children}
        {EndIcon && (
          <span className={styles.iconBox}>
            <EndIcon />
          </span>
        )}
      </Root>
    );
  }
) as PolymorphicForwardRefComponent<'button', 'button' | 'a', PlainButtonProps>;

PlainButton.displayName = 'PlainButton';
